import { useConnections, useIntegrationApp } from '@membranehq/react';
import { format } from 'date-fns';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Option } from 'src/components/SelectBox';
import {
  decodeTagValue,
  isEncodedTagValue,
} from 'src/components/tag/tag.helpers';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest, { AuthedRequest } from 'src/hooks/useAuthedRequest';
import { useCampaigns } from 'src/hooks/useCampaigns';
import { Agent } from 'src/interfaces/agent.interface';
import type {
  DayOfWeek,
  WorkflowContactWindow,
  WorkflowStatus,
} from 'src/interfaces/workflows.interface';
import {
  Campaign,
  CampaignAlreadyExistsError,
  CampaignSchedule,
  CampaignValidationError,
  CommunicationChannel,
  CrmCampaignConfig,
  EmailCampaignConfig,
  FollowUpCommunicationConfig,
  SmsCampaignConfig,
  UpdateCampaignDto,
} from 'src/services/campaigns/campaigns.interface';
import { getCampaignAgentIds } from 'src/services/campaigns/campaigns.service';
import {
  IntegrationAppActionKey,
  IntegrationKey,
  NON_AUTO_SYNCABLE_PROVIDERS,
} from 'src/services/integration-app.service';
import { processPhoneNumber } from 'src/utils/number';
import { getErrorCode, getHttpStatus } from 'src/utils/requestUtils';

export const EVENT_EXPRESSION_ROOT = 'event';

export type IntegrationAuthenticationFailureResponse = {
  statusCode: number;
  error: 'IntegrationAuthenticationFailure';
  message?: string;
};

export type FollowUpCommunicationForm = {
  id: string;
  daysAfter?: number;
  contactVia: CommunicationChannel;

  // Phone call related properties
  agentOption?: Option;

  // Email related properties
  emailSubject?: string;
  emailBody?: string;

  // SMS related properties
  smsBody?: string;
};

type CallAutomationScheduleForm = {
  days: DayOfWeek[];
  startTime: string;
  endTime: string;
  timeZoneOption: Option;
};

export type CampaignForm = {
  name: string;
  referenceId: string;
  description?: string;
  /**
   * Server-backed workflow status.
   *
   * Protected field: WizardContext will keep this in sync with the server,
   * and ignore any attempts to update it via `setCampaignAutomation`.
   */
  status?: WorkflowStatus;
  /** Campaign start date (yyyy-MM-dd). */
  campaignStartDate?: string;
  /** IANA timezone for interpreting campaignStartDate. */
  campaignStartDateTz?: string;
  contactWindowId?: string;
  schedule?: CallAutomationScheduleForm; // deprecated
  initialCallAfterOption: Option;
  agentOption?: Option;
  fromNumberOptions: Option[];
  followUpCommunications: FollowUpCommunicationForm[];
  crmProviderOption: Option;
  emailProviderOption: Option;
  smsProviderOption: Option;
  recordCommunicationsInCrm?: boolean;
  isAutoSyncContactsEnabled?: boolean;
  fromEmail?: string;
  smsFromNumber?: string;
};

export const initialCallAfterOptions: Option[] = [
  { value: '0', label: 'Call Immediately' },
  { value: '1', label: 'Call 1 Hour Later' },
  { value: '2', label: 'Call 2 Hours Later' },
  { value: '4', label: 'Call 4 Hours Later' },
  { value: '24', label: 'Call 1 Day Later' },
  { value: '168', label: 'Call 1 Week Later' },
];

export const timeZoneOptions: Option[] = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
];

export const CRM_PROVIDER_OPTIONS: Option[] = [
  { value: IntegrationKey.ACTIVECAMPAIGN, label: 'ActiveCampaign' },
  { value: IntegrationKey.GOHIGHLEVEL, label: 'GoHighLevel' },
  { value: IntegrationKey.HUBSPOT, label: 'Hubspot' },
  { value: IntegrationKey.MONDAY, label: 'Monday.com' },
  { value: IntegrationKey.SALESFORCE, label: 'Salesforce' },
  { value: IntegrationKey.TLD_CRM, label: 'Total Lead Domination' },
];

export const DEFAULT_CRM_PROVIDER_OPTION: Option = {
  label: 'Select a provider',
  value: '',
};

export const DEFAULT_EMAIL_PROVIDER_OPTION: Option = {
  label: 'Select a provider',
  value: '',
};

export const DEFAULT_SMS_PROVIDER_OPTION: Option = {
  label: 'Select a provider',
  value: '',
};

export const AGENT_VALUE_SAME_AS_INITIAL_CALL = 'same';

export const AGENT_OPTION_SAME_AS_INITIAL_CALL: Option = {
  value: AGENT_VALUE_SAME_AS_INITIAL_CALL,
  label: 'Same as initial call',
};

export const generateListElementId = () =>
  Math.random().toString(36).substring(2, 9);

const TOTAL_STEPS = 2;

const initialCampaignAutomationState: CampaignForm = {
  name: '',
  referenceId: '',
  description: '',
  status: undefined,
  campaignStartDate: undefined,
  campaignStartDateTz: undefined,
  contactWindowId: undefined,
  schedule: undefined,
  initialCallAfterOption: initialCallAfterOptions[0],
  agentOption: undefined,
  fromNumberOptions: [],
  followUpCommunications: [],
  crmProviderOption: DEFAULT_CRM_PROVIDER_OPTION,
  emailProviderOption: DEFAULT_EMAIL_PROVIDER_OPTION,
  smsProviderOption: DEFAULT_SMS_PROVIDER_OPTION,
  recordCommunicationsInCrm: false,
  isAutoSyncContactsEnabled: false,
  fromEmail: '',
  smsFromNumber: '',
};

type WizardContextType = {
  isEditing: boolean;
  isInitializing: boolean;
  currentStep: number;
  totalSteps: number;
  campaignAutomation: CampaignForm;
  campaign: Campaign | null;
  contactWindow: WorkflowContactWindow | null;

  nextStep: () => void;
  prevStep: () => void;
  resetWizard: () => void;
  setCampaignAutomation: React.Dispatch<React.SetStateAction<CampaignForm>>;
  saveCampaignAutomation: (
    automationOverride?: CampaignForm,
  ) => Promise<CampaignForm | false>;
  activateCampaign: () => Promise<CampaignForm | false>;
  deactivateCampaign: () => Promise<CampaignForm | false>;
  setCampaign: (campaign: Campaign) => void;

  // Email provider related properties
  emailProviders: Option[];

  // SMS provider related properties
  smsProviders: Option[];
};

const WizardContext = createContext<WizardContextType | undefined>(undefined);

export function getTodayLocal() {
  return format(new Date(), 'yyyy-MM-dd');
}

const fetchAgentsById = async (
  agentIds: string[],
  authedRequest: AuthedRequest,
): Promise<Agent[]> => {
  return Promise.all(
    agentIds.map((agentId) => authedRequest.get(`/agents/${agentId}`)),
  );
};

export function WizardProvider({
  children,
  isEditing = false,
  editReferenceId,
  getNewCampaignDefaults,
}: {
  children: React.ReactNode;
  isEditing?: boolean;
  editReferenceId?: string;
  getNewCampaignDefaults?: (base: CampaignForm) => CampaignForm;
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [campaignAutomation, setCampaignAutomation] = useState<CampaignForm>(
    () => {
      const base = { ...initialCampaignAutomationState };
      return getNewCampaignDefaults ? getNewCampaignDefaults(base) : base;
    },
  );
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [contactWindow, setContactWindow] =
    useState<WorkflowContactWindow | null>(null);
  const [isContactWindowInitialized, setIsContactWindowInitialized] =
    useState(true);
  const [isEditingStateInitialized, setIsEditingStateInitialized] =
    useState(false);
  const [isEmailProvidersInitialized, setIsEmailProvidersInitialized] =
    useState(false);
  const [isSmsProvidersInitialized, setIsSmsProvidersInitialized] =
    useState(false);
  const authedRequest = useAuthedRequest();
  const campaignsService = useCampaigns();
  const notification = useNotification();
  const hasInitiatedCampaignLoad = useRef(false);

  // Email provider state
  const [emailProviders, setEmailProviders] = useState<Option[]>([]);
  const [emailConnectionId, setEmailConnectionId] = useState<string>();

  // SMS provider state
  const [smsProviders, setSmsProviders] = useState<Option[]>([]);
  const [smsConnectionId, setSmsConnectionId] = useState<string>();

  const integrationApp = useIntegrationApp();
  const { connections: integrationAppConnections } = useConnections();

  /**
   * Protected setter: callers cannot update `campaignAutomation.status`.
   * WizardContext can still update it via `setCampaignAutomation`.
   */
  const setCampaignAutomationProtected: WizardContextType['setCampaignAutomation'] =
    useCallback((action) => {
      setCampaignAutomation((prev) => {
        const next =
          typeof action === 'function'
            ? (action as (prev: CampaignForm) => CampaignForm)(prev)
            : action;

        const { status: _ignoredStatus, ...rest } = next;
        void _ignoredStatus;
        return {
          ...(rest as Omit<CampaignForm, 'status'>),
          status: prev.status,
        };
      });
    }, []);

  useEffect(() => {
    const connection = integrationAppConnections.find(
      (conn) =>
        conn.integration?.key === campaignAutomation.emailProviderOption.value,
    );
    if (connection) {
      setEmailConnectionId(connection.id);
    }
  }, [integrationAppConnections, campaignAutomation.emailProviderOption.value]);

  useEffect(() => {
    const connection = integrationAppConnections.find(
      (conn) =>
        conn.integration?.key === campaignAutomation.smsProviderOption.value,
    );
    if (connection) {
      setSmsConnectionId(connection.id);
    }
  }, [integrationAppConnections, campaignAutomation.smsProviderOption.value]);

  // Load email providers
  const getEmailProviders = async () => {
    try {
      const actions = await integrationApp?.actions.findAll();
      const sendEmailAction = actions?.find(
        (action) => action.key === IntegrationAppActionKey.SEND_EMAIL,
      );
      if (sendEmailAction) {
        const action = await integrationApp?.action(sendEmailAction.id).get();
        const integrations = action?.appliedToIntegrations ?? [];
        setEmailProviders(
          integrations
            .filter(({ integration }) => integration.key != null)
            .map(({ integration }) => ({
              label: integration.name,
              value: integration.key!,
            })),
        );
      }
      setIsEmailProvidersInitialized(true);
    } catch (error) {
      console.error('Failed to load email providers:', error);
      notification.error(
        'Failed to load email providers. Some features may be unavailable.',
      );
      setIsEmailProvidersInitialized(true); // Still mark as initialized to prevent infinite loading
    }
  };

  const getSmsProviders = async () => {
    try {
      const actions = await integrationApp?.actions.findAll();
      const sendMessageAction = actions?.find(
        (action) => action.key === IntegrationAppActionKey.SEND_MESSAGE,
      );
      if (sendMessageAction) {
        const action = await integrationApp?.action(sendMessageAction.id).get();
        const integrations = action?.appliedToIntegrations ?? [];
        const providers: Option[] = [];
        for (const { integration } of integrations) {
          if (integration.key) {
            providers.push({
              label: integration.name,
              value: integration.key,
            });
          }
        }
        setSmsProviders(providers);
      }
      setIsSmsProvidersInitialized(true);
    } catch (error) {
      console.error('Failed to load SMS providers:', error);
      notification.error(
        'Failed to load SMS providers. Some features may be unavailable.',
      );
      setIsSmsProvidersInitialized(true); // Still mark as initialized to prevent infinite loading
    }
  };

  useEffect(() => {
    getEmailProviders();
    getSmsProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const contactWindowId = campaignAutomation.contactWindowId;
    if (!contactWindowId) {
      setContactWindow(null);
      setIsContactWindowInitialized(true);
      return;
    }

    setIsContactWindowInitialized(false);
    (async () => {
      try {
        const cw = (await authedRequest.get(
          `/workflow_contact_windows/${contactWindowId}`,
        )) as WorkflowContactWindow;
        setContactWindow(cw);
      } catch {
        setContactWindow(null);
      } finally {
        setIsContactWindowInitialized(true);
      }
    })();
  }, [authedRequest, campaignAutomation.contactWindowId]);

  // Load existing campaign when editing
  useEffect(() => {
    const loadCampaign = async () => {
      if (
        isEditing &&
        editReferenceId &&
        !isEditingStateInitialized &&
        !hasInitiatedCampaignLoad.current
      ) {
        hasInitiatedCampaignLoad.current = true;
        try {
          const existingCampaign =
            await campaignsService.fetchCampaign(editReferenceId);

          // Extract all the agents referenced in the campaign
          const agentIds = getCampaignAgentIds(existingCampaign);
          const campaignAgents = await fetchAgentsById(agentIds, authedRequest);

          initializePrimaryCampaignFormProperties(
            existingCampaign,
            campaignAgents,
          );
          setCampaign(existingCampaign);
        } catch (error) {
          console.error('Failed to load campaign:', error);
          notification.error('Failed to load campaign');
          setIsEditingStateInitialized(true);
        }
      }
    };

    loadCampaign();
  }, [
    isEditing,
    editReferenceId,
    isEditingStateInitialized,
    authedRequest,
    notification,
    campaignsService,
  ]);

  const initializeCommunicationProviderOptions = useCallback(
    (campaign: Campaign, emailProviders: Option[], smsProviders: Option[]) => {
      const { emailCampaignConfig: emailConfig, smsCampaignConfig: smsConfig } =
        campaign;

      let emailProviderOption = DEFAULT_EMAIL_PROVIDER_OPTION;
      let smsProviderOption = DEFAULT_SMS_PROVIDER_OPTION;

      // Get email provider from campaign
      if (emailConfig?.provider) {
        const emailProvider = emailProviders.find(
          (option) => option.value === emailConfig?.provider,
        );
        emailProviderOption = emailProvider || DEFAULT_EMAIL_PROVIDER_OPTION;
      }

      // Get SMS provider from the campaign
      if (smsConfig?.provider) {
        const smsProvider = smsProviders.find(
          (option) => option.value === smsConfig?.provider,
        );
        smsProviderOption = smsProvider || DEFAULT_SMS_PROVIDER_OPTION;
      }

      setCampaignAutomation({
        ...campaignAutomation,
        emailProviderOption,
        smsProviderOption,
      });
    },
    [campaignAutomation, setCampaignAutomation],
  );

  // This is the final step of initializng the edit state. This has to be done after the email and SMS providers
  // are initialized
  useEffect(() => {
    const loadCommunicationProviderOptions = async () => {
      if (
        isEditing &&
        campaign &&
        isEmailProvidersInitialized &&
        isSmsProvidersInitialized &&
        !isEditingStateInitialized
      ) {
        initializeCommunicationProviderOptions(
          campaign,
          emailProviders,
          smsProviders,
        );
        setIsEditingStateInitialized(true);
      }
    };
    loadCommunicationProviderOptions();
  }, [
    isEditing,
    campaign,
    isEditingStateInitialized,
    isEmailProvidersInitialized,
    isSmsProvidersInitialized,
    emailProviders,
    smsProviders,
    initializeCommunicationProviderOptions,
  ]);

  const nextStep = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const resetWizard = () => {
    setCurrentStep(1);
    setCampaignAutomation({ ...initialCampaignAutomationState });
    setCampaign(null);
  };

  const initializePrimaryCampaignFormProperties = (
    fetchedCampaign: Campaign,
    campaignAgents: Agent[],
  ) => {
    let schedule: CallAutomationScheduleForm | undefined = undefined;
    if (!fetchedCampaign.contactWindowId && fetchedCampaign.schedule) {
      const campaignSchedule = fetchedCampaign.schedule;
      const timeZoneOption = timeZoneOptions.find(
        (option) => option.value === campaignSchedule.timeZone,
      );
      schedule = {
        days: campaignSchedule.days,
        startTime: campaignSchedule.startTime,
        endTime: campaignSchedule.endTime,
        timeZoneOption: timeZoneOption || timeZoneOptions[0],
      };
    }

    const initialCallAfterOption =
      initialCallAfterOptions.find(
        (option) =>
          option.value ===
          String(fetchedCampaign.initialCallConfig.scheduleAfterHours),
      ) || initialCallAfterOptions[0];

    const fromNumberOptions: Option[] =
      fetchedCampaign.callCampaignConfig.fromNumbers.map((tagOrNumber) => {
        if (isEncodedTagValue(tagOrNumber)) {
          return {
            value: tagOrNumber,
            label: decodeTagValue(tagOrNumber),
          };
        }
        return {
          value: tagOrNumber,
          label: processPhoneNumber(tagOrNumber),
        };
      });

    const initialCallAgentOption: Option | undefined = {
      value: fetchedCampaign.initialCallConfig.agentId,
      label:
        campaignAgents.find(
          (a) => a.id === fetchedCampaign.initialCallConfig.agentId,
        )?.name || (fetchedCampaign.initialCallConfig.agentId as string),
    };

    const followUpCommunications: FollowUpCommunicationForm[] = [];
    let followupCount = 0;
    for (const followupConfig of fetchedCampaign.followUpCommunicationConfigs) {
      const daysAfter = followupConfig.scheduleAfterDays;
      followupCount++;

      if (followupConfig.contactVia === CommunicationChannel.EMAIL) {
        followUpCommunications.push({
          id: `followup-${followupCount}`,
          daysAfter,
          contactVia: CommunicationChannel.EMAIL,
          emailBody: followupConfig.emailConfig?.body,
          emailSubject: followupConfig.emailConfig?.subject,
        });
      } else if (followupConfig.contactVia === CommunicationChannel.SMS) {
        followUpCommunications.push({
          id: `followup-${followupCount}`,
          daysAfter,
          contactVia: CommunicationChannel.SMS,
          smsBody: followupConfig.smsConfig?.body,
        });
      } else {
        // This is a call step
        const agentId = followupConfig.callConfig?.agentId;
        let agentOption = AGENT_OPTION_SAME_AS_INITIAL_CALL;
        if (agentId !== initialCallAgentOption?.value) {
          agentOption = {
            value: agentId as string,
            label:
              campaignAgents.find((a) => a.id === agentId)?.name ||
              (agentId as string),
          };
        }

        followUpCommunications.push({
          id: `followup-${followupCount}`,
          daysAfter,
          contactVia: CommunicationChannel.CALL,
          agentOption,
        });
      }
    }

    let crmProviderOption = DEFAULT_CRM_PROVIDER_OPTION;
    let recordCommunicationsInCrm = false;
    let isAutoSyncContactsEnabled = false;
    if (fetchedCampaign.crmCampaignConfig !== undefined) {
      const crmConfig = fetchedCampaign.crmCampaignConfig;
      const crmProvider = CRM_PROVIDER_OPTIONS.find(
        (option) => option.value === crmConfig.provider,
      );
      crmProviderOption = crmProvider || DEFAULT_CRM_PROVIDER_OPTION;
      recordCommunicationsInCrm = crmConfig.recordCommunications;
      const isNonAutoSyncable = NON_AUTO_SYNCABLE_PROVIDERS.includes(
        crmConfig.provider as IntegrationKey,
      );
      isAutoSyncContactsEnabled =
        !isNonAutoSyncable && Boolean(crmConfig.isAutoSyncContactsEnabled);
    }

    setCampaignAutomation((prev: CampaignForm) => ({
      ...prev,
      name: fetchedCampaign.name,
      referenceId: fetchedCampaign.referenceId,
      description: fetchedCampaign.description,
      status: fetchedCampaign.status,
      campaignStartDate: fetchedCampaign.campaignStartDate,
      campaignStartDateTz: fetchedCampaign.campaignStartDateTz,
      contactWindowId: fetchedCampaign.contactWindowId,
      schedule,
      initialCallAfterOption,
      agentOption: initialCallAgentOption,
      fromNumberOptions,
      followUpCommunications,
      crmProviderOption,
      // emailProviderOption - filled in later
      // smsProviderOption - filled in later
      recordCommunicationsInCrm,
      isAutoSyncContactsEnabled,
      fromEmail: fetchedCampaign.emailCampaignConfig?.fromEmail,
      smsFromNumber: fetchedCampaign.smsCampaignConfig?.fromNumber,
    }));
  };

  const buildCampaign = (
    automation: CampaignForm = campaignAutomation,
  ): UpdateCampaignDto => {
    const initialAgentid = automation.agentOption?.value as string;

    let schedule: CampaignSchedule | undefined = undefined;
    if (!automation.contactWindowId && automation.schedule) {
      schedule = {
        days: automation.schedule.days,
        startTime: automation.schedule.startTime,
        endTime: automation.schedule.endTime,
        timeZone: automation.schedule.timeZoneOption.value,
      };
    }

    let hasEmailFollowup = false;
    let hasSmsFollowup = false;
    const followUpCommunicationConfigs: FollowUpCommunicationConfig[] =
      automation.followUpCommunications.map((communication) => {
        if (communication.daysAfter === undefined) {
          throw new CampaignValidationError(
            'Days after is required for follow up communications',
          );
        }

        if (communication.contactVia === CommunicationChannel.EMAIL) {
          hasEmailFollowup = true;
          return {
            scheduleAfterDays: communication.daysAfter,
            contactVia: CommunicationChannel.EMAIL,
            emailConfig: {
              subject: communication.emailSubject || '',
              body: communication.emailBody || '',
            },
          };
        } else if (communication.contactVia === CommunicationChannel.SMS) {
          hasSmsFollowup = true;
          return {
            scheduleAfterDays: communication.daysAfter,
            contactVia: CommunicationChannel.SMS,
            smsConfig: {
              body: communication.smsBody || '',
            },
          };
        } else {
          let agentId = communication.agentOption?.value as string;
          if (agentId === AGENT_VALUE_SAME_AS_INITIAL_CALL) {
            agentId = initialAgentid;
          }

          return {
            scheduleAfterDays: communication.daysAfter,
            contactVia: CommunicationChannel.CALL,
            callConfig: {
              agentId,
            },
          };
        }
      });

    const emailCampaignConfig: EmailCampaignConfig | undefined =
      hasEmailFollowup
        ? {
            provider: automation.emailProviderOption.value,
            fromEmail: automation.fromEmail || '',
            connectionId: emailConnectionId || '',
          }
        : undefined;

    const smsCampaignConfig: SmsCampaignConfig | undefined = hasSmsFollowup
      ? {
          provider: automation.smsProviderOption.value,
          fromNumber: automation.smsFromNumber || '',
          connectionId: smsConnectionId || '',
        }
      : undefined;

    const crmProvider = automation.crmProviderOption.value;
    const isNonAutoSyncable = NON_AUTO_SYNCABLE_PROVIDERS.includes(
      crmProvider as IntegrationKey,
    );
    const crmCampaignConfig: CrmCampaignConfig | undefined = crmProvider
      ? {
          provider: crmProvider,
          recordCommunications: automation.recordCommunicationsInCrm || false,
          isAutoSyncContactsEnabled:
            !isNonAutoSyncable && Boolean(automation.isAutoSyncContactsEnabled),
        }
      : undefined;

    const payload: UpdateCampaignDto = {
      referenceId: automation.referenceId,
      name: automation.name,
      description: automation.description,

      campaignStartDate: automation.campaignStartDate,
      campaignStartDateTz: automation.campaignStartDateTz,
      contactWindowId: automation.contactWindowId,
      schedule,

      initialCallConfig: {
        agentId: initialAgentid,
        scheduleAfterHours: parseInt(
          automation.initialCallAfterOption.value,
          10,
        ),
      },
      followUpCommunicationConfigs,

      callCampaignConfig: {
        fromNumbers: automation.fromNumberOptions.map((option) => option.value),
      },
      emailCampaignConfig,
      smsCampaignConfig,
      crmCampaignConfig,
    };

    return payload;
  };

  const trackEmailFromCampaign = async (campaign: Campaign): Promise<void> => {
    try {
      const fromEmail = campaign.emailCampaignConfig?.fromEmail;
      if (fromEmail) {
        await authedRequest.post('/emails/recent', {
          email: fromEmail.trim().toLowerCase(),
        });
      }
    } catch (error) {
      // Email tracking is not critical - don't fail the campaign operation
      console.warn('Failed to track email from campaign:', error);
    }
  };

  const saveCampaignAutomation = async (
    automationOverride?: CampaignForm,
  ): Promise<CampaignForm | false> => {
    const nextAutomation = automationOverride ?? campaignAutomation;
    const campaignPayload = buildCampaign(nextAutomation);

    if (isEditing) {
      try {
        const updatedCampaign =
          await campaignsService.updateCampaign(campaignPayload);

        setCampaign(updatedCampaign);
        setCampaignAutomation((prev) => ({
          ...prev,
          status: updatedCampaign.status,
        }));

        // Track email address for recent emails
        await trackEmailFromCampaign(updatedCampaign);

        return {
          ...nextAutomation,
          status: updatedCampaign.status,
        };
      } catch (error) {
        if (error instanceof CampaignValidationError) {
          notification.error(error.message);
        } else {
          const statusCode = getHttpStatus(error);
          const errorCode = getErrorCode(error);
          if (
            statusCode === 401 &&
            errorCode === 'IntegrationAuthenticationFailure'
          ) {
            notification.error(
              'Your connection with the CRM is not authenticated. Please authenticate the requested CRM and try again.',
            );
          } else {
            console.error('Error updating campaign:', error);
            notification.error('Failed to update campaign');
          }
        }
        return false;
      }
    } else {
      try {
        const createdCampaign =
          await campaignsService.createCampaign(campaignPayload);
        setCampaign(createdCampaign);
        setCampaignAutomation((prev) => ({
          ...prev,
          status: createdCampaign.status,
        }));
        await trackEmailFromCampaign(createdCampaign);
        return {
          ...nextAutomation,
          status: createdCampaign.status,
        };
      } catch (error) {
        if (error instanceof CampaignValidationError) {
          notification.error(error.message);
        } else if (error instanceof CampaignAlreadyExistsError) {
          notification.error(
            'A campaign already exists with this ID. Please enter a unique Campaign ID.',
          );
        } else {
          const statusCode = getHttpStatus(error);
          const errorCode = getErrorCode(error);
          if (
            statusCode === 401 &&
            errorCode === 'IntegrationAuthenticationFailure'
          ) {
            notification.error(
              'Your connection with the CRM is not authenticated. Please authenticate the requested CRM and try again.',
            );
          } else {
            console.error('Error creating campaign:', error);
            notification.error('Failed to create campaign');
          }
        }
        return false;
      }
    }
  };

  const activateCampaign = async (): Promise<CampaignForm | false> => {
    const referenceId =
      campaign?.referenceId ||
      campaignAutomation.referenceId ||
      editReferenceId;
    if (!referenceId) {
      notification.error('Can not activate a campaign that does not exist');
      return false;
    }

    try {
      const updatedCampaign =
        await campaignsService.activateCampaign(referenceId);
      setCampaign(updatedCampaign);
      setCampaignAutomation((prev) => ({
        ...prev,
        status: updatedCampaign.status,
      }));
      return {
        ...campaignAutomation,
        status: updatedCampaign.status,
      };
    } catch (error) {
      console.error('Error activating campaign:', error);
      notification.error('Failed to activate campaign');
      return false;
    }
  };

  const deactivateCampaign = async (): Promise<CampaignForm | false> => {
    const referenceId =
      campaign?.referenceId ||
      campaignAutomation.referenceId ||
      editReferenceId;
    if (!referenceId) {
      notification.error('Can not deactivate a campaign that does not exist');
      return false;
    }

    try {
      const updatedCampaign =
        await campaignsService.deactivateCampaign(referenceId);
      setCampaign(updatedCampaign);
      setCampaignAutomation((prev) => ({
        ...prev,
        status: updatedCampaign.status,
      }));
      return {
        ...campaignAutomation,
        status: updatedCampaign.status,
      };
    } catch (error) {
      console.error('Error deactivating campaign:', error);
      notification.error('Failed to deactivate campaign');
      return false;
    }
  };

  return (
    <WizardContext.Provider
      value={{
        isEditing,
        isInitializing:
          (isEditing && !isEditingStateInitialized) ||
          !isContactWindowInitialized ||
          !isEmailProvidersInitialized ||
          !isSmsProvidersInitialized,
        currentStep,
        totalSteps: TOTAL_STEPS,
        campaignAutomation,
        campaign,
        contactWindow,
        nextStep,
        prevStep,
        resetWizard,
        setCampaignAutomation: setCampaignAutomationProtected,
        saveCampaignAutomation,
        activateCampaign,
        deactivateCampaign,
        setCampaign,
        // Email provider values
        emailProviders,

        // SMS provider values
        smsProviders,
      }}
    >
      {children}
    </WizardContext.Provider>
  );
}

export function useWizard() {
  const context = useContext(WizardContext);
  if (context === undefined) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
}
